import React, { useEffect } from 'react';
import { SEO } from '../components/SEO';
import { FaDownload, FaArrowRight, FaExclamationTriangle } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const Anywhere = () => {
  // Reset scroll position when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const downloadSteps = [
    "Descargar el archivo Parcheaw",
    "Descomprimir el archivo en el equipo de cómputo",
    "Ejecutar la aplicación Parcheaw descargada desde el paquete",
    "Ejecutar OPUS",
    "En caso de que aún no pueda conectarse la licencia Anywhere, favor de solicitar soporte con su Distribuidor Autorizado"
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Conectividad Anywhere | Ecosoft"
        description="Soluciona tus problemas de conectividad con tu licencia Anywhere. Herramienta oficial para restaurar la comunicación con las licencias Anywhere de OPUS."
        name="Anywhere"
        type="website"
      />
      
      {/* Hero Section */}
      <section id="hero" className="relative h-[450px] bg-[#003171]">
        <div 
          className="absolute inset-0 bg-cover bg-center" 
          style={{ 
            backgroundImage: "url('/images/academia opus.jpg')",
            backgroundPosition: "center center",
            backgroundSize: "cover"
          }}
        ></div>
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="container mx-auto px-4 h-full flex items-center pt-14 relative z-10">
          <div className="max-w-2xl text-white">
            <h1 className="text-5xl font-bold mb-4">Conectividad Anywhere</h1>
            <p className="text-xl mb-8">
              Soluciona tus problemas de conectividad con tu licencia Anywhere
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="#download" 
                className="flex items-center bg-[#0066CC] hover:bg-[#004a99] text-white px-6 py-3 rounded-md inline-block transition-colors"
              >
                <FaDownload className="mr-2 text-xl" />
                Descargar Ahora
              </a>
              <Link
                to="/FAQ"
                className="flex items-center bg-white hover:bg-gray-100 text-[#003171] px-6 py-3 rounded-md inline-block transition-colors"
              >
                Preguntas Frecuentes
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Main Content Section */}
      <section id="download" className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-8">
              <h2 className="text-3xl font-bold mb-8 text-center text-[#003171]">Conectividad Anywhere</h2>
              
              <div className="mb-8 bg-yellow-50 border-l-4 border-yellow-400 p-5 rounded-r">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <FaExclamationTriangle className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-base text-yellow-700">
                      Si tienes problemas de comunicación con tu licencia Anywhere, o bien deseas limpiar los parches distribuidos durante la contingencia de los servicios, ejecuta las siguientes instrucciones:
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mb-10">
                <h3 className="text-xl font-semibold mb-4 text-[#003171]">Instrucciones de instalación:</h3>
                <ul className="space-y-4">
                  {downloadSteps.map((step, index) => (
                    <li key={index} className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#0066CC] text-white flex items-center justify-center mr-3 mt-0.5">
                        {index + 1}
                      </div>
                      <p className="text-gray-700">{step}</p>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="text-center">
                <a 
                  href="https://www.ecosoft.com.mx/downloads/parcheaw.zip" 
                  className="inline-flex items-center bg-[#0066CC] hover:bg-[#004a99] text-white px-8 py-4 rounded-md text-lg font-medium transition-colors"
                  download
                >
                  <FaDownload className="mr-2 text-xl" />
                  Descargar Parcheaw.zip
                </a>
                <p className="mt-3 text-sm text-gray-500">Tamaño del archivo: 3.5 MB</p>
              </div>
            </div>
          </div>
          
          <div className="mt-12 bg-gray-50 rounded-lg p-6 border border-gray-200">
            <h3 className="text-xl font-semibold mb-4 text-[#003171]">¿Necesitas ayuda adicional?</h3>
            <p className="mb-4 text-gray-700">
              Si después de aplicar este parche continúas experimentando problemas, contacta a nuestro equipo de soporte técnico o a tu Distribuidor Autorizado.
            </p>
            <div className="flex flex-wrap gap-4 mt-6">
              <Link
                to="/Contact"
                className="flex items-center text-[#0066CC] hover:text-[#004a99] font-medium"
              >
                Contactar Soporte <FaArrowRight className="ml-2" />
              </Link>
              <Link
                to="/Distribuidores"
                className="flex items-center text-[#0066CC] hover:text-[#004a99] font-medium"
              >
                Distribuidores Autorizados <FaArrowRight className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Call to Action Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-3xl font-bold mb-6 text-[#003171]">Explora más recursos de soporte</h2>
          <p className="text-lg text-gray-700 mb-8">
            Descubre más herramientas, guías y recursos para aprovechar al máximo tu software OPUS.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-3 text-[#003171]">Academia OPUS</h3>
              <p className="text-gray-600 mb-4">
                Aprende a utilizar OPUS con nuestros videos tutoriales y guías.
              </p>
              <Link
                to="/AcademiaOpus"
                className="text-[#0066CC] hover:text-[#004a99] font-medium flex items-center justify-center"
              >
                Ver videos <FaArrowRight className="ml-2" />
              </Link>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-3 text-[#003171]">Centro de Descargas</h3>
              <p className="text-gray-600 mb-4">
                Descarga las últimas versiones de nuestros productos.
              </p>
              <Link
                to="/Downloads"
                className="text-[#0066CC] hover:text-[#004a99] font-medium flex items-center justify-center"
              >
                Ir a Descargas <FaArrowRight className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Anywhere; 